if (!customElements.get('ugc-video-slider')) {
  const SELECTORS = {
    swiper: '[data-swiper]',
    modal: '[data-modal]',
    modalContent: '.ugc-video-modal__content',
    modalPlay: '[data-modal-play]',
    modalClose: '[data-modal-close]',
    modalOverlay: '[data-modal-overlay]',
    modalPrev: '[data-modal-prev]',
    modalNext: '[data-modal-next]',
    sliderPrev: '[data-slider-prev]',
    sliderNext: '[data-slider-next]',
    videosJson: '[data-videos-json]',
    slide: '[data-ugc-index]',
  };

  const CLASSES = {
    inactive: 'is-inactive',
    playing: 'is-playing',
    open: 'is-open',
    activeVideo: 'is-active',
    hidden: 'is-hidden',
    sliderActive: 'is-slider-active',
  };

  /**
   * Number of videos on each side of the current one kept mounted in the
   * modal with preload="auto". 1 → at most 3 live <video> elements, well
   * below iOS Safari's concurrent media element limit (~16). Preloaded
   * neighbours eliminate the click-to-play delay on prev/next navigation.
   */
  const MOUNT_WINDOW = 1;

  class UgcVideoSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.videosData = [];
      /** @type {Map<number, HTMLVideoElement>} */
      this._videoPool = new Map();
      this._onKeydown = this._onKeydown.bind(this);
      this._onVideoPlay = this._onVideoPlay.bind(this);
      this._onVideoPause = this._onVideoPause.bind(this);
    }

    connectedCallback() {
      this.swiper = this.querySelector(SELECTORS.swiper);
      this.modal = this.querySelector(SELECTORS.modal);
      const jsonEl = this.querySelector(SELECTORS.videosJson);
      if (!this.swiper || !this.modal || !jsonEl) return;

      try {
        this.videosData = JSON.parse(jsonEl.textContent);
      } catch (e) {
        this.videosData = [];
      }

      // Move modal to <body> to escape parent stacking contexts.
      document.body.appendChild(this.modal);

      // Cache modal DOM references once — these were previously looked up
      // on every play/pause/navigate event.
      this.modalContent = this.modal.querySelector(SELECTORS.modalContent);
      this.modalPlayBtn = this.modal.querySelector(SELECTORS.modalPlay);
      this.modalPrevBtn = this.modal.querySelector(SELECTORS.modalPrev);
      this.modalNextBtn = this.modal.querySelector(SELECTORS.modalNext);
      this.modalCloseBtn = this.modal.querySelector(SELECTORS.modalClose);
      this.modalOverlay = this.modal.querySelector(SELECTORS.modalOverlay);

      this.swiperInstance = this._initSwiper();
      this._updateNavVisibility();
      this._bindSliderNav();
      this._bindSlideClick();
      this._bindModalControls();

      document.addEventListener('keydown', this._onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeydown);
      this._unmountAll();
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
      if (this.modal && this.modal.parentNode === document.body) {
        this.modal.remove();
      }
    }

    _initSwiper() {
      if (typeof Swiper === 'undefined') return null;

      const el = this.swiper;
      const speed = parseInt(el.dataset.speed, 10) || 300;
      const perViewMobile = parseFloat(el.dataset.perViewMobile) || 2.3;
      const perViewTablet = parseFloat(el.dataset.perViewTablet) || 3.3;
      const perViewDesktop = parseFloat(el.dataset.perViewDesktop) || 4;
      const betweenMobile = parseInt(el.dataset.betweenMobile, 10) || 8;
      const betweenTablet = parseInt(el.dataset.betweenTablet, 10) || 10;
      const betweenDesktop = parseInt(el.dataset.betweenDesktop, 10) || 12;

      return new Swiper(el, {
        speed,
        watchOverflow: true,
        slidesPerView: perViewMobile,
        spaceBetween: betweenMobile,
        breakpoints: {
          750: {
            slidesPerView: perViewTablet,
            spaceBetween: betweenTablet,
          },
          990: {
            slidesPerView: perViewDesktop,
            spaceBetween: betweenDesktop,
          },
        },
      });
    }

    _updateNavVisibility() {
      const isActive = this.swiperInstance && !this.swiperInstance.isLocked;
      this.classList.toggle(CLASSES.sliderActive, isActive);
    }

    _bindSliderNav() {
      const swiperInstance = this.swiperInstance;
      if (!swiperInstance) return;

      const prevBtn = this.querySelector(SELECTORS.sliderPrev);
      const nextBtn = this.querySelector(SELECTORS.sliderNext);

      const updateNavState = () => {
        if (prevBtn) prevBtn.classList.toggle(CLASSES.inactive, swiperInstance.isBeginning);
        if (nextBtn) nextBtn.classList.toggle(CLASSES.inactive, swiperInstance.isEnd);
        this._updateNavVisibility();
      };

      if (prevBtn) prevBtn.addEventListener('click', () => swiperInstance.slidePrev());
      if (nextBtn) nextBtn.addEventListener('click', () => swiperInstance.slideNext());

      updateNavState();
      swiperInstance.on('slideChange', updateNavState);
      swiperInstance.on('reachBeginning', updateNavState);
      swiperInstance.on('reachEnd', updateNavState);
      swiperInstance.on('resize', updateNavState);
      swiperInstance.on('breakpoint', updateNavState);
    }

    _bindSlideClick() {
      this.swiper.addEventListener('click', (e) => {
        const slide = e.target.closest(SELECTORS.slide);
        if (!slide) return;
        this.openModal(parseInt(slide.dataset.ugcIndex, 10));
      });
    }

    _bindModalControls() {
      this.modalCloseBtn.addEventListener('click', () => this.closeModal());
      this.modalOverlay.addEventListener('click', () => this.closeModal());

      // Delegated click on video → toggle play/pause. Clicks on the play
      // button are handled by its own listener below; they bubble here
      // with tagName !== 'VIDEO' so they don't double-toggle.
      this.modalContent.addEventListener('click', (e) => {
        if (e.target.tagName === 'VIDEO') this._toggleActiveVideo();
      });

      if (this.modalPlayBtn) {
        this.modalPlayBtn.addEventListener('click', () => this._toggleActiveVideo());
      }

      this.modalPrevBtn.addEventListener('click', () => {
        if (this.currentIndex > 0) this.navigateTo(this.currentIndex - 1);
      });

      this.modalNextBtn.addEventListener('click', () => {
        if (this.currentIndex < this.videosData.length - 1) this.navigateTo(this.currentIndex + 1);
      });
    }

    /**
     * Create a <video> element for a given index (idempotent) and insert
     * it into modalContent with preload="auto" so it starts buffering
     * immediately. Returns the pooled element.
     * @param {number} index
     * @returns {HTMLVideoElement|null}
     */
    _mountVideo(index) {
      if (this._videoPool.has(index)) return this._videoPool.get(index);

      const data = this.videosData[index];
      if (!data) return null;

      const video = document.createElement('video');
      video.className = 'ugc-video-modal__video';
      video.playsInline = true;
      video.loop = true;
      video.muted = false;
      // `preload` is only a hint — browsers are free to ignore it on
      // mobile/cellular. We also call .load() below to force the resource
      // selection algorithm to run immediately.
      video.preload = 'auto';
      if (data.poster) video.poster = data.poster;

      data.sources.forEach((source) => {
        const srcEl = document.createElement('source');
        srcEl.src = source.url;
        srcEl.type = source.mime_type;
        video.appendChild(srcEl);
      });

      video.addEventListener('play', this._onVideoPlay);
      video.addEventListener('pause', this._onVideoPause);

      // Insert before the play button so the button stays on top.
      if (this.modalPlayBtn && this.modalPlayBtn.parentNode === this.modalContent) {
        this.modalContent.insertBefore(video, this.modalPlayBtn);
      } else {
        this.modalContent.appendChild(video);
      }

      // Force the browser to start the resource selection algorithm now.
      // Without this, Safari in particular may defer loading even with
      // preload="auto" until the element is closer to being played.
      try { video.load(); } catch (e) { /* noop */ }

      this._videoPool.set(index, video);
      return video;
    }

    /**
     * Tear down a pooled <video>, fully releasing its media pipeline.
     * Critical for iOS Safari which caps concurrent media elements.
     * @param {number} index
     */
    _unmountVideo(index) {
      const video = this._videoPool.get(index);
      if (!video) return;

      video.removeEventListener('play', this._onVideoPlay);
      video.removeEventListener('pause', this._onVideoPause);

      try {
        video.pause();
        while (video.firstChild) video.removeChild(video.firstChild);
        video.removeAttribute('src');
        video.load();
      } catch (e) {
        /* element may already be detached */
      }

      video.remove();
      this._videoPool.delete(index);
    }

    _unmountAll() {
      Array.from(this._videoPool.keys()).forEach((idx) => this._unmountVideo(idx));
    }

    /**
     * Ensure videos within MOUNT_WINDOW of activeIdx are mounted and
     * everything else is unmounted.
     * @param {number} activeIdx
     */
    _updateMounts(activeIdx) {
      const total = this.videosData.length;
      if (total === 0) return;

      const minIdx = Math.max(0, activeIdx - MOUNT_WINDOW);
      const maxIdx = Math.min(total - 1, activeIdx + MOUNT_WINDOW);

      // Unmount anything outside the window first (frees decoders before
      // mounting new ones — important on iOS).
      Array.from(this._videoPool.keys()).forEach((idx) => {
        if (idx < minIdx || idx > maxIdx) this._unmountVideo(idx);
      });

      for (let i = minIdx; i <= maxIdx; i++) {
        this._mountVideo(i);
      }
    }

    /**
     * Show + play the video at the given index, hide + pause all others.
     * Assumes _updateMounts has already been called.
     * @param {number} index
     */
    _activateVideo(index) {
      const target = this._videoPool.get(index);
      if (!target) return;

      this._videoPool.forEach((video, idx) => {
        if (idx === index) return;
        video.classList.remove(CLASSES.activeVideo);
        try { video.pause(); } catch (e) { /* noop */ }
      });

      target.classList.add(CLASSES.activeVideo);
      const p = target.play();
      if (p) p.catch(() => {});
    }

    /**
     * Navigate to a video within an already-open modal.
     * @param {number} index
     */
    navigateTo(index) {
      if (index < 0 || index >= this.videosData.length) return;
      this.currentIndex = index;
      this._updateMounts(index);
      this._activateVideo(index);
      this._updateArrowsVisibility();
    }

    openModal(index) {
      if (isNaN(index) || index < 0 || index >= this.videosData.length) return;
      this.currentIndex = index;
      this._updateMounts(index);
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.classList.add(CLASSES.open);
      document.body.style.overflow = 'hidden';
      this._activateVideo(index);
      this._updateArrowsVisibility();
    }

    closeModal() {
      const active = this._videoPool.get(this.currentIndex);
      if (active) { try { active.pause(); } catch (e) { /* noop */ } }
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.classList.remove(CLASSES.open);
      document.body.style.overflow = '';
      // Release all media pipelines when the modal is closed.
      this._unmountAll();
    }

    _updateArrowsVisibility() {
      this.modalPrevBtn.classList.toggle(CLASSES.hidden, this.currentIndex === 0);
      this.modalNextBtn.classList.toggle(
        CLASSES.hidden,
        this.currentIndex === this.videosData.length - 1
      );
    }

    _onKeydown(e) {
      if (!this.modal.classList.contains(CLASSES.open)) return;
      if (e.key === 'Escape') this.closeModal();
      else if (e.key === 'ArrowLeft' && this.currentIndex > 0) this.navigateTo(this.currentIndex - 1);
      else if (e.key === 'ArrowRight' && this.currentIndex < this.videosData.length - 1) {
        this.navigateTo(this.currentIndex + 1);
      }
    }

    _toggleActiveVideo() {
      const active = this._videoPool.get(this.currentIndex);
      if (!active) return;
      if (active.paused) {
        active.play().catch(() => {});
      } else {
        active.pause();
      }
    }

    _onVideoPlay(e) {
      if (e.target === this._videoPool.get(this.currentIndex)) {
        this.modalContent.classList.add(CLASSES.playing);
      }
    }

    _onVideoPause(e) {
      if (e.target === this._videoPool.get(this.currentIndex)) {
        this.modalContent.classList.remove(CLASSES.playing);
      }
    }
  }

  customElements.define('ugc-video-slider', UgcVideoSlider);
}
